import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import api from 'state/axios';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Tooltip,
  Chip,
  Avatar,
  Divider,
  useTheme,
} from '@mui/material';
import { AutoAwesome, Send, DeleteOutline, Close, SmartToy } from '@mui/icons-material';
import { setAICopilotOpen } from 'state';
import useAIContext from 'hooks/useAIContext';

const DRAWER_WIDTH = 420;

const QUICK_PROMPTS = [
  "Any flights delayed today?",
  "Crew available today?",
  "Summarize tomorrow's flight schedule",
  "Suggest crew for flight",
];

const AICopilotPanel = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const open = useSelector((state) => state.isAICopilotOpen);
  const context = useAIContext();

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const handleSend = useCallback(async (text) => {
    const content = (text || input).trim();
    if (!content || loading) return;

    const userMsg = { role: 'user', content };
    const nextMessages = [...messages, userMsg];

    setMessages(nextMessages);
    setInput('');
    setLoading(true);

    try {
      const res = await api.post(
        '/ai/chat',
        { messages: nextMessages, context }
      );
      const { reply, toolsUsed = [] } = res.data;
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: reply, toolsUsed },
      ]);
    } catch (err) {
      toast.error('AI is not responding. Check that Ollama is running.');
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  }, [
    input, 
    messages, 
    loading, 
    context,
  ]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isAIBubble = theme.palette.mode === 'dark'
    ? 'rgba(99, 102, 241, 0.15)'
    : 'rgba(99, 102, 241, 0.08)';

  const isUserBubble = theme.palette.mode === 'dark'
    ? theme.palette.grey[700]
    : theme.palette.grey[200];


  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => dispatch(setAICopilotOpen(false))}
      variant="temporary"
      sx={{
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}
      >
        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
          <SmartToy sx={{ fontSize: 18 }} />
        </Avatar>
        <Box flex={1}>
          <Typography variant="body2" fontWeight={600}>DigitAir AI</Typography>
          <Typography variant="caption" color="text.secondary">
            {context.page !== 'general' ? `Context: ${context.page}` : 'Virtual assistant'}
          </Typography>
        </Box>
        <Tooltip title="Clear chat history">
          <IconButton size="small" onClick={() => setMessages([])} disabled={loading}>
            <DeleteOutline fontSize="small" />
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={() => dispatch(setAICopilotOpen(false))}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      {/* Messages */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1 }}>
        {messages.length === 0 ? (
          <Box pt={2}>
            <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mb={2}>
              Ask about flight, crew, OTP, ...
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={0.75} justifyContent="center">
              {QUICK_PROMPTS.map((q) => (
                <Chip
                  key={q}
                  label={q}
                  size="small"
                  variant="outlined"
                  clickable
                  onClick={() => handleSend(q)}
                  sx={{ fontSize: '0.72rem' }}
                />
              ))}
            </Box>
          </Box>
        ) : (
          messages.map((msg, i) => (
            <Box
              key={i}
              display="flex"
              flexDirection={msg.role === 'user' ? 'row-reverse' : 'row'}
              alignItems="flex-start"
              gap={1}
              mb={1.5}
            >
              {msg.role === 'assistant' && (
                <Avatar sx={{ width: 26, height: 26, bgcolor: 'primary.main', flexShrink: 0, mt: 0.25 }}>
                  <SmartToy sx={{ fontSize: 14 }} />
                </Avatar>
              )}
              <Box sx={{ maxWidth: '85%' }}>
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: msg.role === 'user' ? '8px 8px 2px 8px' : '8px 8px 8px 2px',
                    backgroundColor: msg.role === 'user' ? isUserBubble : isAIBubble,
                    border: msg.role === 'assistant' ? '1px solid rgba(99,102,241,0.2)' : 'none',
                    wordBreak: 'break-word',
                  }}
                >
                  {msg.role === 'assistant' ? (
                    <Box
                      sx={{
                        '& p': { m: 0, fontSize: '0.85rem' },
                        '& p + p': { mt: 0.5 },
                        '& table': { borderCollapse: 'collapse', width: '100%', fontSize: '0.78rem' },
                        '& th, & td': { border: '1px solid', borderColor: 'divider', px: 0.75, py: 0.4 },
                        '& th': { bgcolor: 'action.hover', fontWeight: 600 },
                        '& code': { bgcolor: 'action.hover', borderRadius: '3px', px: 0.5, fontFamily: 'monospace', fontSize: '0.8em' },
                        '& ul, & ol': { m: 0, pl: 2 },
                        '& li': { mb: 0.25, fontSize: '0.85rem' },
                        '& strong': { fontWeight: 600 },
                      }}
                    >
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {msg.content}
                      </ReactMarkdown>
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>
                      {msg.content}
                    </Typography>
                  )}
                </Box>
                {msg.toolsUsed?.length > 0 && (
                  <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                    {msg.toolsUsed.map((t) => (
                      <Chip
                        key={t}
                        label={t}
                        size="small"
                        icon={<AutoAwesome sx={{ fontSize: '10px !important' }} />}
                        sx={{ fontSize: '0.65rem', height: 18 }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          ))
        )}

        {loading && (
          <Box display="flex" alignItems="center" gap={1} mb={1} pl={0.5}>
            <Avatar sx={{ width: 26, height: 26, bgcolor: 'primary.main' }}>
              <SmartToy sx={{ fontSize: 14 }} />
            </Avatar>
            <Box
              sx={{
                px: 1.5,
                py: 0.75,
                borderRadius: '8px 8px 8px 2px',
                backgroundColor: isAIBubble,
                border: '1px solid rgba(99,102,241,0.2)',
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
              }}
            >
              <CircularProgress size={12} />
              <Typography variant="caption" color="text.secondary">
                AI is thinking...
              </Typography>
            </Box>
          </Box>
        )}

        <div ref={bottomRef} />
      </Box>

      <Divider />

      {/* Input */}
      <Box sx={{ px: 1.5, py: 1, flexShrink: 0 }}>
        <Box display="flex" alignItems="flex-end" gap={1}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            size="small"
            variant="outlined"
            placeholder="Ask about flights, crew, schedules..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.85rem' } }}
          />
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleSend()}
            disabled={loading || !input.trim()}
            sx={{ mb: 0.25 }}
          >
            {loading ? <CircularProgress size={18} /> : <Send fontSize="small" />}
          </IconButton>
        </Box>
      </Box>
    </Drawer>
  );
};

export default AICopilotPanel;
